<script setup>
// My Agenda Builder Preferences — a right-side drawer for editing the same
// free-text preferences the Schedule Builder wizard uses. Opened from the My
// Schedule action bar (agenda.vue). Reuses useScheduleBuilder()'s prefs CRUD
// (loadPrefs GET / savePrefs PUT → /api/v1/schedule/preferences); this drawer
// only owns its own editable draft + the drawer chrome.
//
// useScheduleBuilder is imported explicitly (not the bare-global auto-import
// style used in agenda.vue) so it can be mocked per-path in the component
// test — same convention as find-a-session.vue.
// reka-ui's Dialog a11y primitives must be imported explicitly (same as
// drawers/session.vue & speaker.vue) — they are NOT auto-imported, so relying
// on the bare tags renders them as unresolved custom elements that leak the
// title/description as visible text at the top of the drawer.
import { useScheduleBuilder } from '@/composables/useScheduleBuilder'
import { form_field_error_border_2026, form_error_text_2026 } from '~~/shared/utilities/ui'

const open = defineModel('open')

// Local editable draft — kept out of useScheduleBuilder()'s shared `prefs`
// state until Save, so typing here never leaks into the wizard mid-edit
// (same reasoning as schedule-builder.vue's own draftPrefs).
const draft = ref('')
// What was loaded, so we can tell whether the user has actually changed anything.
const original = ref('')
const loading = ref(false)
const submitting = ref(false)
const error = ref('')
// 'form' | 'exitConfirm' — the exit-confirm view asks before discarding edits.
const view = ref('form')

// Clear the required-field error once the user starts typing.
watch(draft, () => { if (error.value && draft.value.trim()) error.value = '' })

const dirty = computed(() => draft.value !== original.value)
// Save is blocked until the user changes something (UNB-594), and an empty field
// can't be saved (UNB-595) — the builder needs some text to tailor the schedule.
const canSave = computed(() => dirty.value && !!draft.value.trim() && !loading.value && !submitting.value)

// Load the current saved prefs each time the drawer opens, so the textarea
// always reflects what's persisted (the user may have edited via the builder
// since last time). Best-effort: a failed load just leaves the field as-is.
watch(open, async (isOpen) => {
    // Hide the HubSpot chat/agent widget while the drawer is open — it sits at
    // the max z-index and otherwise covers the drawer's controls. Same approach
    // as drawers/agenda-filters.vue.
    const hs = document.getElementById('hubspot-messages-iframe-container')
    if (hs) hs.style.display = isOpen ? 'none' : ''

    if (!isOpen) return
    error.value = ''
    view.value = 'form'
    loading.value = true
    try {
        const builder = useScheduleBuilder()
        await builder.loadPrefs()
        draft.value = builder.prefs.value || ''
        original.value = draft.value
        // First-open pre-fill: when the user has no saved prefs, offer an LLM
        // summary generated from their Bizzabo profile seed — same behavior as
        // the Schedule Builder wizard (schedule-builder.vue). Best-effort:
        // suggestPrefs() may return '' (no profile data) or fail, which just
        // leaves the field empty. `original` stays empty so the suggestion reads
        // as an unsaved draft the user can Save or discard.
        if (!draft.value) {
            try {
                const suggested = await builder.suggestPrefs()
                draft.value = suggested?.prefs || ''
            } catch { /* best-effort pre-fill only */ }
        }
    } catch (e) {
        // leave whatever's there
    } finally {
        loading.value = false
    }
})

// Closing with unsaved edits routes through the exit-confirm view (687674) rather
// than discarding silently. A clean close (no edits) just closes.
const attemptClose = () => {
    if (dirty.value) { view.value = 'exitConfirm' }
    else { open.value = false }
}
const discardAndClose = () => {
    draft.value = original.value
    view.value = 'form'
    open.value = false
}

const save = async () => {
    // Testing affordance: the field rejects a blank submit (UNB-595), so there's
    // no way to reset saved prefs from the UI. Typing the literal "clear" blanks
    // the SAVED preferences instead — which drops prefsText back to empty and lets
    // you re-test the builder's first-open LLM regeneration (the ranker still
    // falls back to the Bizzabo profile seed). Case-insensitive.
    //
    // It also resets the promo banner's open-count (Asana 141204). That count is
    // per-device localStorage, not account state, so clearing prefs alone left the
    // banner permanently retired on that browser after three opens — leaving no
    // way back to the builder's entry point, which is most of what this affordance
    // is for.
    const isClearCommand = draft.value.trim().toLowerCase() === 'clear'

    // Required (UNB-595): don't save an empty field — surface an error instead.
    if (!isClearCommand && !draft.value.trim()) {
        // Blunt on purpose (Asana UNB-595) — see schedule-builder.vue.
        error.value = 'This field is required'
        return
    }
    submitting.value = true
    error.value = ''
    try {
        const toSave = isClearCommand ? '' : draft.value
        await useScheduleBuilder().savePrefs(toSave)
        if (isClearCommand) {
            // Also drop what the builder placed, so "clear" really does put the
            // account back to never-having-built. Resetting prefs and the banner
            // count alone left scheduledGenerated intact, which kept the Schedule
            // Preferences affordance on screen — the asymmetry Nick hit trying to
            // re-test this (UNB-583). The endpoint re-seals the session cookie, so
            // a refresh here is what makes the UI catch up.
            await useScheduleBuilder().clearGenerated()
            useScheduleBuilder().resetInteractions()
            await useUserSession().fetch()
            // The agenda listens for this to re-fetch its feed; the cleared
            // sessions are gone from the schedule now.
            useNuxtApp().$bus?.emit?.('schedule:changed', { removed: true, cleared: true })
        }
        draft.value = toSave
        original.value = toSave
        open.value = false
    } catch (e) {
        error.value = 'Something went wrong saving your preferences. Please try again.'
    } finally {
        submitting.value = false
    }
}

// Right drawer, pink surface, capped at the comp's 595px (full-bleed on
// mobile). Same shell/ui-slot pattern as drawers/session.vue.
const drawerStyles = {
    content: 'bg-[var(--pink)] border-0 ring-0 rounded-none p-0 w-screen max-w-[95vw] md:max-w-[595px]',
    overlay: 'bg-[var(--purple)]/80 backdrop-blur-xs',
    container: 'gap-0 bg-transparent',
    header: 'p-0',
    title: 'sr-only',
    description: 'sr-only',
    body: 'p-0',
}

// Save Changes — outline pill, border/text --magenta, hover-invert to filled
// (project convention; Figma has no hover variant). Confirmed values from
// node 4583:181898: rounded-[50px], px-[20px] py-[10px], Serif Book 17px,
// tracking-[0.34px].
const saveBtnCls = 'inline-flex items-center gap-[10px] rounded-[50px] border border-(--magenta) text-(--magenta) px-[20px] py-[10px] font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer transition-colors duration-200 hover:bg-(--magenta) hover:text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-(--magenta) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)'

// Close link — Body/Small (Link): Sans Light 14px, --darker-pink #7d4566,
// tracking-[0.28px], dotted underline (node 4583:181897). Same treatment as
// the other 2026 modal/drawer Close links.
const closeLinkCls = 'self-start font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px] text-[#7d4566] underline decoration-dotted underline-offset-[3px] cursor-pointer bg-transparent border-none p-0 transition-colors duration-200 hover:text-(--purple) hover:no-underline'

// Textarea — white, border --dark-pink, rounded-[10px], px-[26px] py-[22px],
// Sans Light 15px --magenta, resize-y (the native resize handle stands in for
// the comp's bottom-right glyph). Node 4583:181892/181893.
const textareaBase = 'w-full max-w-[514px] h-[234px] bg-white border rounded-[10px] px-[26px] py-[22px] text-[15px] leading-[23px] tracking-[0.3px] font-hubspot-sans font-light text-(--magenta) placeholder:text-(--dark-pink) resize-y focus:outline-none'

// The stroke turns red while the error is up, matching the login form
// (Asana UNB-595). Swapped rather than appended: two competing border-colour
// utilities resolve by stylesheet order, not class order.
const textareaCls = computed(() => [
    textareaBase,
    error.value ? form_field_error_border_2026 : 'border-(--dark-pink)',
])
</script>

<template>
    <!-- `dismissible`, not `dismissable`: that's UDrawer's real prop name, and the
         misspelling fell through as a plain HTML attribute, leaving the drawer
         permanently dismissible — an outside click or Esc discarded unsaved edits
         silently. Blocking dismiss while dirty lets `close:prevent` route those
         two paths through the same exit-confirm the ✕ and Close link already use. -->
    <UDrawer
        v-model:open="open"
        direction="right"
        :dismissible="!dirty"
        :handle="false"
        @close:prevent="attemptClose"
        :ui="drawerStyles"
        title="My Agenda Builder Preferences"
        description="Edit the preferences the schedule builder uses to personalize your schedule."
        class="schedule-preferences-drawer font-hubspot-sans"
    >
        <template #body>
            <!-- Title/description come from UDrawer's `title`/`description` props
                 above. A hand-rolled reka `DialogTitle` here instead injects
                 `DialogRootContext`, which vaul's drawer doesn't provide — it
                 threw on every open in production. -->

            <div class="relative min-h-full px-[41px] pt-[47px] pb-[40px]">
                <!-- ✕ close — mirrors the session drawer's close button (37px
                     circle, magenta outline, hover-invert). -->
                <button
                    type="button"
                    data-test="prefs-drawer-close-x"
                    aria-label="Close preferences"
                    class="absolute top-[40px] right-[40px] inline-flex items-center justify-center size-[37px] rounded-full border border-(--magenta) bg-transparent text-(--magenta) hover:bg-(--magenta) hover:text-white transition-colors cursor-pointer"
                    @click="view === 'exitConfirm' ? (view = 'form') : attemptClose()"
                >
                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                </button>

                <!-- Unsaved-changes prompt (687674) — same confirm treatment as My Event. -->
                <FragmentsDialogsConfirmPanel
                    v-if="view === 'exitConfirm'"
                    class="pt-[40px]"
                    headline="Are you sure you want to close without saving your changes?"
                    body="All changes will be discarded when you close without saving."
                    primary-label="Save Changes"
                    secondary-label="Close Without Saving"
                    primary-variant="filled"
                    @primary="() => { view = 'form'; save() }"
                    @secondary="discardAndClose"
                />

                <div v-else class="flex flex-col max-w-[514px]">
                    <!-- Sparkle (24px) — node 4583:181899, fill --darker-pink. -->
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" class="size-[24px] text-[#7d4566]">
                        <path d="M24 14.9487C24 15.1662 23.8235 15.3426 23.6077 15.3426C20.1626 15.3426 16.0855 19.4189 16.0855 22.8633C16.0855 23.0808 15.909 23.2572 15.6932 23.2572C15.4773 23.2572 15.3009 23.0808 15.3009 22.8633C15.2991 19.4189 11.222 15.3426 7.77692 15.3426C7.55935 15.3426 7.38461 15.1662 7.38461 14.9487C7.38461 14.7311 7.56106 14.5547 7.77692 14.5547C11.222 14.5547 15.2991 10.4784 15.2991 7.03576C15.2991 6.81824 15.4756 6.64183 15.6914 6.64183C15.9073 6.64183 16.0838 6.81824 16.0838 7.03576C16.0838 10.4784 20.1609 14.5547 23.606 14.5547C23.8235 14.5547 23.9983 14.7311 23.9983 14.9487H24Z" fill="currentColor" />
                        <path d="M11.0769 6.64126C11.0769 6.78627 10.9593 6.90388 10.8154 6.90388C8.51869 6.90388 5.80057 9.62144 5.80057 11.9177C5.80057 12.0627 5.68293 12.1803 5.53903 12.1803C5.39513 12.1803 5.2775 12.0627 5.2775 11.9177C5.27636 9.62144 2.55823 6.90388 0.261534 6.90388C0.116491 6.90388 0 6.78627 0 6.64126C0 6.49624 0.117633 6.37863 0.261534 6.37863C2.55823 6.37863 5.27636 3.66107 5.27636 1.36599C5.27636 1.22097 5.39399 1.10337 5.53789 1.10337C5.68179 1.10337 5.79942 1.22097 5.79942 1.36599C5.79942 3.66107 8.51755 6.37863 10.8142 6.37863C10.9593 6.37863 11.0758 6.49624 11.0758 6.64126H11.0769Z" fill="currentColor" />
                    </svg>

                    <!-- Heading — Header/H3, Serif Book 36px --magenta, leading-1.2. -->
                    <h2 class="mt-[6px] font-hubspot-serif font-normal text-[36px] leading-[1.2] text-(--magenta) m-0">
                        My Agenda Builder Preferences
                    </h2>

                    <!-- Subhead — Sans Medium 16px --magenta, leading-[26px], tracking-[0.32px]. -->
                    <p class="mt-[20px] font-hubspot-sans font-medium text-[16px] leading-[26px] tracking-[0.32px] text-(--magenta) m-0">
                        My information, topics or HubSpot products of interest and things I’m hoping to takeaway from UNBOUND:
                    </p>

                    <textarea
                        v-model="draft"
                        data-test="prefs-drawer-textarea"
                        :disabled="loading"
                        :class="['mt-[35px]', textareaCls]"
                        placeholder="Tell us about yourself, your interests, and what you're hoping to get out of UNBOUND"
                    />

                    <!-- max-w matches the field's own cap, so the message stays
                         left-aligned WITH the field rather than the column
                         (Asana UNB-595). -->
                    <p v-if="error" data-test="prefs-drawer-error" :class="[form_error_text_2026, 'max-w-[514px]']">{{ error }}</p>

                    <button
                        type="button"
                        data-test="prefs-drawer-save"
                        :disabled="!canSave"
                        :class="['mt-[40px] self-start', saveBtnCls]"
                        @click="save"
                    >
                        {{ submitting ? 'Saving…' : 'Save Changes' }}
                    </button>

                    <button
                        type="button"
                        data-test="prefs-drawer-close"
                        :class="['mt-[20px]', closeLinkCls]"
                        @click="attemptClose"
                    >
                        Close
                    </button>
                </div>
            </div>
        </template>
    </UDrawer>
</template>
